import React, { useRef, useState } from "react";
import { api } from "../lib/api";
import { AiDisclaimerGate } from "./AiDisclaimer";
import { usePlan } from "./UpgradeGate";
import { Sparkles, X, Send, Loader2 } from "lucide-react";

const QUICK_PROMPTS = [
  "What does Margin (0–1) mean?",
  "Suggest a CPM bid for my budget",
  "Which channels fit my objective?",
  "Is my frequency cap right?",
];

const renderMsg = (text) => {
  const html = String(text || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/^[-•] /gm, "• ");
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
};

export const Copilot = ({ context }) => {
  const [open, setOpen] = useState(false);
  const plan = usePlan();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: q }]);
    try {
      const { data } = await api.post("/copilot/ask", { question: q, form: context || {}, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "copilot", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "copilot", content: "Sorry — I hit a snag. Try again in a moment." }]);
    } finally {
      setBusy(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="copilot-fab"
          className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold text-sm shadow-2xl shadow-cyan-500/25 hover:opacity-90 transition-opacity">
          <Sparkles className="w-4 h-4" /> Ask TotalReach
        </button>
      )}
      {open && (!plan?.gated || plan?.features?.ai) && <AiDisclaimerGate onDecline={() => setOpen(false)} />}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-3rem)] rounded-2xl border border-white/10 bg-[#0a0e1a] shadow-2xl flex flex-col overflow-hidden" style={{ height: "520px" }} data-testid="copilot-panel">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-cyan-400/10 to-blue-500/5">
            <div className="text-sm font-display font-semibold flex items-center gap-2 text-white">
              <Sparkles className="w-4 h-4 text-cyan-300" /> Ask TotalReach
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-white" data-testid="copilot-close">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2.5" data-testid="copilot-messages">
            {messages.length === 0 && !busy && (
              <div>
                <div className="text-xs text-slate-400 leading-relaxed">I can explain any field on this page and suggest settings based on what you've entered so far. Try:</div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {QUICK_PROMPTS.map((p) => (
                    <button key={p} onClick={() => send(p)} data-testid="copilot-quick-prompt"
                      className="text-[11px] px-2.5 py-1.5 rounded-full border border-white/10 text-slate-400 hover:border-cyan-400/30 hover:text-cyan-300 transition-colors text-left">
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[90%] rounded-xl px-3 py-2 text-xs whitespace-pre-wrap leading-relaxed ${
                  m.role === "user" ? "bg-cyan-400/15 border border-cyan-400/20 text-cyan-50" : "bg-white/[0.04] border border-white/10 text-slate-200"}`}
                  data-testid={`copilot-msg-${m.role}-${i}`}>
                  {renderMsg(m.content)}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="rounded-xl px-3 py-2 text-[11px] bg-white/[0.04] border border-white/10 text-slate-400 inline-flex items-center gap-2" data-testid="copilot-thinking">
                  <Loader2 className="w-3 h-3 animate-spin text-cyan-300" /> Thinking…
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="p-2.5 border-t border-white/10">
            <div className="flex gap-1.5">
              <input value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about any field or setting…"
                data-testid="copilot-input"
                className="flex-1 bg-white/5 border border-white/10 rounded-full px-3.5 py-2 text-xs focus:outline-none focus:border-cyan-400/50 placeholder:text-slate-600" />
              <button onClick={() => send()} disabled={busy || !input.trim()} data-testid="copilot-send"
                className="w-8 h-8 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 flex items-center justify-center disabled:opacity-40">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
